import React, { useState } from 'react';
import '../styles/AgeVerification.css';

const AgeVerification = ({ onAgeVerified, onAgeRejected }) => {
  const [birthDate, setBirthDate] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  const validateAge = () => {
    setError('');
    
    if (!birthDate) {
      setError('Please enter your date of birth');
      return false;
    }

    const birth = new Date(birthDate);
    const today = new Date();
    
    // Check if date is valid
    if (birth > today) {
      setError('Birth date cannot be in the future');
      return false;
    }
    
    // Check if date is reasonable (not more than 120 years ago)
    const maxAge = new Date();
    maxAge.setFullYear(maxAge.getFullYear() - 120);
    if (birth < maxAge) {
      setError('Please enter a valid birth date');
      return false;
    }

    const age = calculateAge(birthDate);
    
    if (age < 18) {
      setError('You must be at least 18 years old to use this service');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsVerifying(true);
    setError('');

    if (validateAge()) {
      const age = calculateAge(birthDate);
      
      // Small delay to show verification process
      setTimeout(() => {
        setIsVerifying(false);
        onAgeVerified && onAgeVerified({ birthDate, age });
      }, 500);
    } else {
      setTimeout(() => {
        setIsVerifying(false);
        if (calculateAge(birthDate) < 18 && birthDate) {
          onAgeRejected && onAgeRejected({ 
            birthDate, 
            age: calculateAge(birthDate),
            reason: 'Under 18 years old'
          });
        }
      }, 500);
    }
  };

  const handleDateChange = (e) => {
    setBirthDate(e.target.value);
    setError(''); // Clear error when user starts typing
  };

  // Calculate max date (today)
  const today = new Date();
  const maxDate = today.toISOString().split('T')[0];
  
  // Calculate reasonable min date (120 years ago)
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 120);
  const minDateString = minDate.toISOString().split('T')[0];

  return (
    <div className="age-verification-container">
      <div className="age-verification-card">
        <div className="age-verification-header">
          <h2>Age Verification Required</h2>
          <p>To comply with applicable laws and protect minors, you must be at least 18 years old to use this service.</p>
        </div>

        <form className="age-verification-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="birthDate">Date of Birth</label>
            <input
              type="date"
              id="birthDate"
              name="birthDate"
              value={birthDate}
              onChange={handleDateChange}
              min={minDateString}
              max={maxDate}
              required
              disabled={isVerifying}
              className={`form-input ${error ? 'error' : ''}`}
              aria-describedby={error ? 'birth-date-error' : undefined}
            />
            {error && (
              <div id="birth-date-error" className="error-message" role="alert">
                {error}
              </div>
            )}
          </div>

          <div className="age-verification-info">
            <div className="info-icon">ℹ️</div>
            <div className="info-text">
              <strong>Privacy Notice:</strong> Your date of birth is used only for age verification and is not stored or shared with third parties.
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="verify-button"
              disabled={isVerifying || !birthDate}
            >
              {isVerifying ? (
                <>
                  <span className="spinner"></span>
                  Verifying...
                </>
              ) : (
                'Verify Age'
              )}
            </button>
          </div>

          <div className="legal-notice">
            <small>
              By verifying your age, you confirm that you are at least 18 years old and agree to our terms of service. 
              If you are under 18, please have a parent or guardian create an account on your behalf.
            </small>
          </div>
        </form>

        <div className="under-18-section">
          <h3>Under 18?</h3>
          <p>
            If you&apos;re under 18 years old, you can still access our educational content with proper parental supervision. 
            Please have a parent or guardian create an account and supervise your use of the platform.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AgeVerification;